import React from 'react';
import {
    TableRow,
    TableCell,
    Checkbox,
    IconButton,
    Typography,
    Avatar,
    Box,
    Chip,
    Tooltip,
    Stack,
} from '@mui/material';
import { Tune, Inventory2 } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const InventoryTableRow = ({
    item,
    index = 0,
    isSelected,
    onRowCheckboxClick,
    onMenuOpen,
    onAdjust,
}) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng.startsWith('ar');

    const product = item.product || {};
    const variant = item.variant || null;
    const warehouse = item.warehouse || {};

    const productName =
        product?.name?.[lng] ||
        product?.name?.en ||
        product?.name?.ar ||
        product?.name ||
        '-';
    const sku = variant?.sku || product?.sku;
    const variantLabel =
        variant?.name?.[lng] || variant?.name?.en || variant?.name?.ar || variant?.name;

    const onHand = item.on_hand ?? 0;
    const reserved = item.reserved ?? 0;
    const available = item.available ?? Math.max(0, onHand - reserved);

    return (
        <TableRow hover selected={isSelected}>
            <TableCell padding="checkbox">
                <Checkbox checked={isSelected} onClick={(e) => onRowCheckboxClick(e, item.id)} />
            </TableCell>

            <TableCell align="start">{index + 1}</TableCell>

            {/* Product */}
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                        src={product?.image?.thumb || product?.image_url || undefined}
                        variant="rounded"
                        sx={{ width: 40, height: 40, bgcolor: 'action.hover' }}
                    >
                        <Inventory2 fontSize="small" />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 1.3 }}>
                            {productName}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                            {sku && (
                                <Typography variant="caption" color="text.secondary">
                                    SKU: {sku}
                                </Typography>
                            )}
                            {variantLabel && (
                                <Typography variant="caption" color="primary.main">
                                    {variantLabel}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                </Box>
            </TableCell>

            {/* Warehouse */}
            <TableCell align="center">
                <Typography variant="body2">{warehouse?.name || '-'}</Typography>
            </TableCell>

            {/* On Hand */}
            <TableCell align="center">
                <Typography variant="body2" fontWeight="bold">{onHand}</Typography>
            </TableCell>

            {/* Reserved */}
            <TableCell align="center">
                <Typography
                    variant="body2"
                    fontWeight="medium"
                    color={reserved > 0 ? 'warning.main' : 'text.secondary'}
                >
                    {reserved}
                </Typography>
            </TableCell>

            {/* Available */}
            <TableCell align="center">
                <Chip
                    size="small"
                    label={available}
                    color={available > 0 ? 'success' : 'error'}
                    variant="tonal"
                    sx={{ fontWeight: 'bold', borderRadius: '6px', minWidth: 50 }}
                />
            </TableCell>

            {/* Last Update */}
            <TableCell align="center">
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {item.updated_at ? dayjs(item.updated_at).format('YYYY-MM-DD HH:mm') : '-'}
                </Typography>
            </TableCell>

            {/* Actions */}
            <TableCell align="center">
                <Stack direction="row" spacing={0.25} justifyContent="center">
                    <Tooltip title={isArabic ? 'تعديل المخزون' : 'Adjust stock'}>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                onAdjust?.(item);
                            }}
                        >
                            <Tune fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </TableCell>
        </TableRow>
    );
};

export default InventoryTableRow;
